/* ═══════════════════════════════════════════════════════════════════════
   effects-tier.js — MASTERWORKS COMPOSITOR (v6.8.0)
   ───────────────────────────────────────────────────────────────────────
   The artwork carries the soul; this file stays calm.

   As of v6.8.0 the art is no longer what a visitor lands on. A fresh
   visit opens on the cosmos starfield (cosmos-stars.js). The paintings
   enter only when someone *chooses* a tier — a deliberate act, rewarded
   with a deliberate reveal. Within that browsing session the choice
   sticks; the next fresh visit starts on the cosmos again.

   Responsibilities
     • Flip html[data-art-mode] to "art" on a user tier selection
     • Preload and crossfade the two backdrop layers
     • Update (and show/hide) the credit line, read from #cosmos
     • A whisper of mouse parallax on desktop

   Reduced motion: static art, instant swaps, no parallax.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var root = document.getElementById('cosmos');
  if (!root || !root.classList.contains('art-backdrop')) return;

  var layers = {
    a: root.querySelector('[data-art-layer="a"]'),
    b: root.querySelector('[data-art-layer="b"]')
  };
  if (!layers.a || !layers.b) return;

  var html = document.documentElement;
  var credit = document.getElementById('artCredit');
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;
  var VALID = ['daybreak', 'dusk', 'terminal', 'ember'];
  var ART_KEY = 'lux_heresy_art_mode';
  /* Attribution is single-sourced from Theme settings and published on
     #cosmos as data-credit-<tier> (see snippets/cosmos.liquid). This file
     no longer carries its own copy of the four strings — that duplication
     is what let them drift out of step with the paintings. */
  function creditParts(tier) {
    var raw = root.getAttribute('data-credit-' + tier);
    if (!raw) return null;
    var bits = raw.split('::');
    return {
      artist: (bits[0] || '').trim(),
      title: (bits[1] || '').trim(),
      year: (bits[2] || '').trim()
    };
  }
  var active = 'a';
  var currentTier = null;

  /* ── art mode ─────────────────────────────────────────────────────── */
  function artMode() {
    return html.getAttribute('data-art-mode') === 'art' ? 'art' : 'cosmos';
  }

  function setArtMode(mode) {
    if (artMode() === mode) return;
    html.setAttribute('data-art-mode', mode);
    try { sessionStorage.setItem(ART_KEY, mode); } catch (e) { /* private mode */ }
    window.dispatchEvent(new CustomEvent('artModeChanged', { detail: { mode: mode } }));
  }

  function artUrl(tier) {
    return root.getAttribute('data-art-' + tier) || root.getAttribute('data-art-dusk') || '';
  }

  function currentTierAttr() {
    return html.getAttribute('data-tier-theme') ||
           (document.body && document.body.getAttribute('data-tier-theme')) || 'dusk';
  }

  function setLayer(el, tier) {
    el.style.backgroundImage = 'url("' + artUrl(tier).replace(/"/g, '%22') + '")';
  }

  function setCredit(tier) {
    if (!credit) return;
    var c = artMode() === 'art' ? creditParts(tier) : null;
    if (!c || !c.artist) { credit.hidden = true; return; }

    /* Artist roman, title italic, year roman — the way a gallery label
       sets it. Built from text nodes so nothing here can inject markup. */
    credit.textContent = '';
    credit.appendChild(document.createTextNode(c.artist));
    if (c.title) {
      credit.appendChild(sep());
      var em = document.createElement('em');
      em.textContent = c.title;
      credit.appendChild(em);
    }
    if (c.year) {
      credit.appendChild(sep());
      credit.appendChild(document.createTextNode(c.year));
    }
    credit.hidden = false;

    function sep() {
      var s = document.createElement('span');
      s.className = 'art-credit-sep';
      s.textContent = '\u00b7';
      return s;
    }
  }

  /* ── crossfade ────────────────────────────────────────────────────── */
  function show(tier, instant) {
    if (VALID.indexOf(tier) === -1) tier = 'dusk';
    if (tier === currentTier) { setCredit(tier); return; }
    currentTier = tier;

    var incoming = active === 'a' ? 'b' : 'a';
    var inEl = layers[incoming], outEl = layers[active];

    var apply = function () {
      setLayer(inEl, tier);
      if (instant || reducedMotion) {
        inEl.style.transition = 'none';
        inEl.setAttribute('data-active', 'true');
        outEl.removeAttribute('data-active');
        void inEl.offsetWidth; /* flush */
        inEl.style.transition = '';
      } else {
        inEl.setAttribute('data-active', 'true');
        outEl.removeAttribute('data-active');
      }
      active = incoming;
      setCredit(tier);
    };

    /* preload the incoming artwork so the crossfade lands on pixels */
    var img = new Image();
    var done = false;
    var go = function () { if (!done) { done = true; apply(); } };
    img.onload = go;
    img.onerror = go; /* still swap: tier tint + bg colour carry it */
    img.src = artUrl(tier);
    setTimeout(go, 1600); /* never wait forever */
  }

  /* whisper of parallax — desktop only, honours reduced motion */
  if (finePointer && !reducedMotion) {
    var raf = 0, tx = 0, ty = 0;
    window.addEventListener('mousemove', function (e) {
      tx = (e.clientX / window.innerWidth - 0.5) * 12;
      ty = (e.clientY / window.innerHeight - 0.5) * 8;
      if (!raf) {
        raf = requestAnimationFrame(function () {
          raf = 0;
          root.style.transform = 'translate3d(' + (-tx) + 'px,' + (-ty) + 'px,0) scale(1.02)';
        });
      }
    }, { passive: true });
  }

  /* ── tier changes ─────────────────────────────────────────────────
     origin "user"  → someone clicked a tier card. This is the moment the
                      paintings are allowed in.
     origin "init"  → a page restoring a choice made earlier in the
                      session. Honour whatever art mode that session had;
                      never promote cosmos to art on our own. */
  window.addEventListener('tierThemeChanged', function (e) {
    var detail = e.detail || {};
    var tier = detail.tier || currentTierAttr();
    if (detail.origin === 'user') setArtMode('art');
    if (artMode() === 'art') show(tier, detail.origin !== 'user');
    else setCredit(tier);
  });

  /* Expose for anything else that needs to open the curtain */
  window.LuxHeresy = window.LuxHeresy || {};
  window.LuxHeresy.setArtMode = setArtMode;
  window.LuxHeresy.getArtMode = artMode;

  /* ── init ─────────────────────────────────────────────────────────
     If this page load is mid-session and the visitor had already chosen
     a tier, land straight on that painting with no entrance fade.
     Otherwise leave the cosmos alone. */
  if (artMode() === 'art') {
    show(currentTierAttr(), true);
  } else {
    setCredit(currentTierAttr());
  }

  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      if (!reducedMotion) html.classList.add('art-anim');
    });
  });
})();
